// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { authUtils } from '../utils/auth';
import { apiGet, apiPost } from '../utils/apiClient';
import { savePDFFile, openDB } from './pdfUtils';
import LearningStatsChart from './LearningStatsChart';
import AdminDashboard from './Admin/AdminDashboard';
import './PDFLibrary.css';

interface PDFLibraryProps {
  onPDFSelect: (pdf: any, mode?: string) => void;
  onUploadClick: () => void;
  onShowFeatures: () => void;
}

export default function PDFLibrary({ onPDFSelect, onUploadClick, onShowFeatures }: PDFLibraryProps) {
  const [stats, setStats] = useState<any>(null);
  const [tokenStats, setTokenStats] = useState<any>(null);
  const [storageUsed, setStorageUsed] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('last_accessed'); // last_accessed, file_name, annotations_count
  const [showStatsChart, setShowStatsChart] = useState<boolean>(false);
  const [showPaperSearch, setShowPaperSearch] = useState<boolean>(false);
  const [showAdminPanel, setShowAdminPanel] = useState<boolean>(false);
  const [paperSearchQuery, setPaperSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(12);
  const [pagination, setPagination] = useState<any>(null);
  const [selectedPDF, setSelectedPDF] = useState<any>(null);

  const user = authUtils.getUser();

  useEffect(() => {
    loadPDFLibrary();
    loadTokenStats();
    calculateStorage();
  }, [currentPage]);

  const loadPDFLibrary = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiGet(`/api/my-pdfs?page=${currentPage}&page_size=${pageSize}`) as any;

      if (response.success) {
        // 为每个PDF加载缩略图
        const pdfsWithThumbnails = await Promise.all(
          response.stats.pdf_list.map(async (pdf: any) => {
            try {
              const db = await openDB();
              const transaction = db.transaction(['pdfFiles'], 'readonly');
              const store = transaction.objectStore('pdfFiles');
              const result = await new Promise<any>((resolve) => {
                const req = store.get(pdf.fingerprint);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => resolve(null);
              });

              return { ...pdf, thumbnail: result?.thumbnail };
            } catch (error: any) {
              console.error(`加载缩略图失败 ${pdf.fingerprint}:`, error);
              return pdf;
            }
          })
        );

        setStats({ ...response.stats, pdf_list: pdfsWithThumbnails });
        setPagination(response.stats.pagination);
      } else {
        setError(response.error || '加载失败');
      }
    } catch (err: any) {
      setError('加载PDF文档库失败: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadTokenStats = async () => {
    try {
      const response = await apiGet('/api/token-usage/stats') as any;
      if (response.success) {
        setTokenStats(response.stats);
      }
    } catch (err: any) {
      console.error('加载Token统计失败:', err);
    }
  };

  const calculateStorage = async () => {
    try {
      const db = await openDB();
      const transaction = db.transaction(['pdfFiles'], 'readonly');
      const store = transaction.objectStore('pdfFiles');
      const request = store.getAll();

      request.onsuccess = () => {
        const totalSize = request.result.reduce((sum: number, item: any) => {
          return sum + (item.size || 0);
        }, 0);
        setStorageUsed(totalSize);
      };

      request.onerror = () => {
        console.error('计算存储失败');
      };
    } catch (error: any) {
      console.error('计算存储失败:', error);
    }
  };

  const handleLogout = () => {
    if (window.confirm('确定要退出登录吗？')) {
      authUtils.logout();
    }
  };

  // 点击继续阅读，直接进入会话模式
  const handleReadingClick = (pdf: any) => {
    if (onPDFSelect) {
      onPDFSelect(pdf, 'session');
    }
  };

  // 搜索论文
  const handleSearchPapers = async () => {
    if (!paperSearchQuery.trim()) return;

    setSearching(true);
    setSearchResults([]);

    try {
      const response = await apiPost('/api/search-papers', {
        query: paperSearchQuery
      }) as any;

      if (response.success) {
        setSearchResults(response.results || []);
      } else {
        alert('搜索失败: ' + (response.error || '未知错误'));
      }
    } catch (error: any) {
      console.error('搜索论文失败:', error);
      alert('搜索失败: ' + error.message);
    } finally {
      setSearching(false);
    }
  };

  // 下载论文
  const handleDownloadPaper = async (paper: any) => {
    setDownloading(paper.id);

    try {
      // Step 1: 服务端下载论文并保存到磁盘（不再返回PDF字节）
      const response = await apiPost('/api/download-paper', {
        paper_url: paper.pdf_url,
        paper_title: paper.title
      }) as any;

      if (response.success && response.pdf_info) {
        const fingerprint = response.pdf_info.fingerprint;

        // Step 2: 从服务端二进制接口获取PDF（避免hex编码开销）
        const pdfResp = await fetch(`/api/pdfs/${fingerprint}`, {
          credentials: 'include',
        });

        if (pdfResp.ok) {
          const pdfBlob = await pdfResp.blob();
          const pdfFile = new File([pdfBlob], paper.title + '.pdf', {
            type: 'application/pdf',
            lastModified: Date.now()
          });

          // 保存到IndexedDB
          await savePDFFile(fingerprint, pdfFile);
        }

        alert('论文下载成功！已添加到文档库');
        setShowPaperSearch(false);
        setPaperSearchQuery('');
        setSearchResults([]);
        loadPDFLibrary(); // 刷新文档库
      } else {
        alert('下载失败: ' + (response.error || '未知错误'));
      }
    } catch (error: any) {
      console.error('下载论文失败:', error);
      alert('下载失败: ' + error.message);
    } finally {
      setDownloading(null);
    }
  };

  // 筛选和排序PDF列表
  const getFilteredAndSortedPDFs = (): any[] => {
    if (!stats?.pdf_list) return [];

    let filtered = stats.pdf_list;

    // 搜索过滤
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((pdf: any) =>
        pdf.file_name.toLowerCase().includes(term)
      );
    }

    // 排序
    const sorted = [...filtered].sort((a: any, b: any) => {
      switch (sortBy) {
        case 'file_name':
          return a.file_name.localeCompare(b.file_name);
        case 'annotations_count':
          return (b.annotations_count || 0) - (a.annotations_count || 0);
        case 'last_accessed':
        default:
          return new Date(b.last_accessed).getTime() - new Date(a.last_accessed).getTime();
      }
    });

    return sorted;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;

    return date.toLocaleDateString('zh-CN');
  };

  const filteredPDFs = getFilteredAndSortedPDFs();

  if (loading) {
    return (
      <div className="pdf-library-loading">
        <div className="loading-spinner"></div>
        <p>加载中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-library-error">
        <p>{error}</p>
        <button onClick={loadPDFLibrary}>重试</button>
      </div>
    );
  }

  return (
    <div className="pdf-library">
      {/* 顶部导航栏 */}
      <header className="library-header">
        <div className="header-left">
          <h1>PDF智能学习助手</h1>
          <span className="user-greeting">欢迎回来，{user?.username || '用户'}</span>
        </div>
        <div className="header-right">
          <button
            className="btn-secondary"
            onClick={onShowFeatures}
            style={{ marginRight: '10px' }}
          >
            📢 更新日志
          </button>
          {authUtils.isAdmin() && (
            <button
              className="btn-secondary"
              onClick={() => setShowAdminPanel(true)}
              style={{ marginRight: '10px', backgroundColor: '#e3f2fd', color: '#1976d2', border: '1px solid #90caf9' }}
            >
              ⚙️ 系统管理
            </button>
          )}
          <button className="btn-secondary" onClick={handleLogout}>
            退出登录
          </button>
        </div>
      </header>

      {/* 统计面板 */}
      <div className="stats-panel">
        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-info">
            <div className="stat-value">{stats?.total_pdfs || 0}</div>
            <div className="stat-label">已上传PDF</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-info">
            <div className="stat-value">{stats?.total_annotations || 0}</div>
            <div className="stat-label">标注总数</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🤖</div>
          <div className="stat-info">
            <div className="stat-value">
              {tokenStats?.total?.total_tokens ?
                (tokenStats.total.total_tokens / 1000).toFixed(1) + 'K' :
                '0'}
            </div>
            <div className="stat-label">AI Token消耗</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <div className="stat-value">
              ¥{tokenStats?.total?.total_cost ?
                parseFloat(tokenStats.total.total_cost).toFixed(2) :
                '0.00'}
            </div>
            <div className="stat-label">累计花费</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💾</div>
          <div className="stat-info">
            <div className="stat-value">
              {(storageUsed / 1024 / 1024).toFixed(1)} MB
            </div>
            <div className="stat-label">本地存储</div>
          </div>
        </div>
      </div>

      {/* 显示/隐藏统计图表按钮 */}
      <div className="stats-chart-toggle">
        <button
          className="btn-stats-toggle"
          onClick={() => setShowStatsChart(!showStatsChart)}
        >
          {showStatsChart ? '📊 隐藏详细统计' : '📊 查看详细统计'}
        </button>
      </div>

      {/* 学习统计图表 */}
      {showStatsChart && (
        <div className="stats-chart-container">
          <LearningStatsChart />
        </div>
      )}

      {/* 工具栏 */}
      <div className="library-toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            className="search-input"
            placeholder="搜索PDF文档..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="toolbar-right">
          <select
            className="sort-select"
            value={sortBy}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSortBy(e.target.value)}
          >
            <option value="last_accessed">最近访问</option>
            <option value="file_name">文件名</option>
            <option value="annotations_count">标注数量</option>
          </select>

          <button className="btn-secondary" onClick={() => setShowPaperSearch(true)}>
            搜索论文
          </button>

          <button className="btn-primary" onClick={onUploadClick}>
            上传PDF
          </button>
        </div>
      </div>

      {/* PDF列表 */}
      <div className="pdf-list">
        {filteredPDFs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📄</div>
            <h3>
              {searchTerm
                ? '没有找到匹配的PDF'
                : '还没有上传任何PDF'}
            </h3>
            <p>
              {searchTerm
                ? '尝试其他搜索关键词'
                : '点击"上传PDF"开始您的学习之旅'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={onUploadClick}>
                上传第一个PDF
              </button>
            )}
          </div>
        ) : (
          filteredPDFs.map((pdf: any) => (
            <div key={pdf.fingerprint} className="pdf-card">
              {pdf.thumbnail ? (
                <img
                  src={pdf.thumbnail}
                  alt={pdf.file_name}
                  className="pdf-thumbnail"
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    (target.parentElement?.querySelector('.pdf-icon-fallback') as HTMLElement | null)?.style.setProperty('display', 'flex');
                  }}
                />
              ) : null}
              <div className="pdf-icon pdf-icon-fallback" style={{ display: pdf.thumbnail ? 'none' : 'flex' }}>📄</div>

              <div className="pdf-info">
                <h3 className="pdf-title" title={pdf.file_name}>
                  {pdf.file_name}
                </h3>

                {/* 标签 */}
                {pdf.tags && pdf.tags.length > 0 && (
                  <div className="pdf-tags">
                    {pdf.tags.map((tag: any) => (
                      <span
                        key={tag.id}
                        className="tag-badge"
                        style={{ backgroundColor: tag.color }}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* 学习进度条 */}
                {pdf.learning_progress && pdf.learning_progress.progress_percent > 0 && (
                  <div className="learning-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${pdf.learning_progress.progress_percent}%` }}
                      />
                    </div>
                    <div className="progress-text">
                      已读 {pdf.learning_progress.pages_read}/{pdf.total_pages} 页
                      ({pdf.learning_progress.progress_percent.toFixed(0)}%)
                    </div>
                  </div>
                )}

                <div className="pdf-meta">
                  <span>{pdf.total_pages} 页</span>
                  <span>{formatFileSize(pdf.file_size)}</span>
                  <span>{pdf.annotations_count || 0} 条标注</span>
                  <span>{formatDate(pdf.last_accessed)}</span>
                </div>
              </div>

              <div className="pdf-actions">
                <button
                  className="btn-action btn-primary"
                  onClick={() => handleReadingClick(pdf)}
                >
                  继续阅读
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 分页器 */}
      {pagination && pagination.total_pages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={!pagination.has_prev}
          >
            上一页
          </button>

          <div className="pagination-info">
            第 {pagination.page} / {pagination.total_pages} 页
            <span className="total-count">（共 {stats.total_pdfs} 个文档）</span>
          </div>

          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={!pagination.has_next}
          >
            下一页
          </button>
        </div>
      )}

      {/* 论文搜索对话框 */}
      {showPaperSearch && (
        <div className="modal-overlay" onClick={() => setShowPaperSearch(false)}>
          <div className="modal-content paper-search-modal" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>搜索并下载论文</h2>
              <button className="close-btn" onClick={() => setShowPaperSearch(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="search-box">
                <input
                  type="text"
                  className="paper-search-input"
                  placeholder="输入论文标题、关键词或作者..."
                  value={paperSearchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPaperSearchQuery(e.target.value)}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleSearchPapers()}
                />
                <button
                  className="btn-primary"
                  onClick={handleSearchPapers}
                  disabled={searching || !paperSearchQuery.trim()}
                >
                  {searching ? '搜索中...' : '搜索'}
                </button>
              </div>

              <div className="search-results">
                {searching && <div className="loading-spinner"></div>}

                {!searching && searchResults.length === 0 && paperSearchQuery && (
                  <div className="no-results">
                    <p>未找到相关论文</p>
                  </div>
                )}

                {searchResults.map((paper: any) => (
                  <div key={paper.id} className="paper-result-item">
                    <div className="paper-info">
                      <h3>{paper.title}</h3>
                      <p className="paper-authors">{paper.authors}</p>
                      <p className="paper-meta">
                        {paper.year && <span>{paper.year}</span>}
                        {paper.venue && <span> · {paper.venue}</span>}
                      </p>
                    </div>
                    <div className="paper-actions">
                      <button
                        className="btn-primary"
                        onClick={() => handleDownloadPaper(paper)}
                        disabled={downloading === paper.id}
                      >
                        {downloading === paper.id ? '下载中...' : '下载'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 管理员面板 - 全屏管理后台 */}
      {showAdminPanel && (
        <AdminDashboard onClose={() => setShowAdminPanel(false)} />
      )}
    </div>
  );
}
